import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  CssBaseline,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LPAppBar from '../../app/components/LPAppBar';
import LPDrawer from '../../app/components/LPDrawer';
import ShiftHome from '../../app/components/ShiftHome';
import { useStyles } from '../../assets/styles/DefaultPageStyle';
import CSharpUtils from '../../felles/CSharpUtils';
import * as NV04APICalls from '../../felles/APICalls_NV04';
import FirmNavnId from '../../models/FirmNavnId';
import UISize from '../../models/UISize';
import { fetchSnapshotRequest, clearNV04Data } from '../../store/actions/NV04.actions';
import * as actions from '../../store/actions/index';
import { AppState } from '../../store/rootReducer';

const red = '#d50032';
const green = '#2f7d3d';
const border = '#d9d9d9';
const textMain = '#222222';
const textMuted = '#6f6f6f';
const bgLight = '#f7f7f7';
const cardHeight = 190;
const monthCardHeight = 180;

// The "Bilag Lagerverdi" PDF went into production in April 2026 — there's no real
// warehouse-value data captured before that, so don't offer the download for earlier periods.
const PDF_AVAILABLE_FROM_YEAR = 2026;
const PDF_AVAILABLE_FROM_MONTH = 4;

function isPdfAvailableFor(year: number, month: number) {
  return (
    year > PDF_AVAILABLE_FROM_YEAR ||
    (year === PDF_AVAILABLE_FROM_YEAR && month >= PDF_AVAILABLE_FROM_MONTH)
  );
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currency: 'NOK',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatShort(value: number) {
  if (Math.abs(value) >= 1000000) {
    return `${(value / 1000000).toFixed(1)} mill.`.replace('.', ',');
  }

  return new Intl.NumberFormat('nb-NO', {
    maximumFractionDigits: 0,
  }).format(value);
}

function ChangeIndicator({ change }: { change: number }) {
  const isIncrease = change >= 0;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.25 }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.4,
          borderRadius: '8px',
          backgroundColor: isIncrease ? '#eaf6ec' : '#fdecec',
          color: isIncrease ? green : red,
        }}
      >
        {isIncrease ? (
          <TrendingUpIcon sx={{ fontSize: 16 }} />
        ) : (
          <TrendingDownIcon sx={{ fontSize: 16 }} />
        )}

       <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'inherit' }}>
          {change > 0 ? '+' : ''}
        {change.toFixed(1)}%
        </Typography>
      </Box>

      <Typography sx={{ fontSize: 13, color: textMuted }}>
        mot forrige måned
      </Typography>
    </Box>
  );
}

function SummaryCard({
  title,
  value,
  accent = red,
  change,
  subtitle,
}: {
  title: string;
  value: string;
  accent?: string;
  change?: number;
  subtitle?: string;
}) {
  return (
    <Card
      sx={{
        borderRadius: '10px',
        border: `1px solid ${border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ height: 6, backgroundColor: accent }} />

      <CardContent sx={{ p: 2.5 }}>
        <Typography sx={{ fontSize: 14, color: textMuted, mb: 1 }}>
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: 30,
            fontWeight: 700,
            color: textMain,
            lineHeight: 1.15,
          }}
        >
          {value}
        </Typography>

        {typeof change === 'number' ? (
          <ChangeIndicator change={change} />
        ) : (
          <Typography sx={{ fontSize: 13, color: textMuted, mt: 1 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function WarehouseCard({ code, value }: { code: string; value: number }) {
  return (
    <Card
      sx={{
        borderRadius: '10px',
        border: `1px solid ${border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        height: cardHeight,
        width: '100%',
      }}
    >
      <CardContent sx={{ p: 2.25, height: '100%' }}>
        <Typography sx={{ fontSize: 13, color: textMuted, mb: 1 }}>
          Lager {code}
        </Typography>

        <Typography sx={{ fontSize: 24, fontWeight: 700, color: textMain }}>
          {formatShort(value)}
        </Typography>

        <Typography sx={{ fontSize: 12, color: textMuted, mt: 0.75 }}>
          {formatCurrency(value)}
        </Typography>
      </CardContent>
    </Card>
  );
}

function MonthCard({
  month,
  value,
  isCurrent = false,
  changePercent,
  changeValue,
  onClick,
  onDownloadPdf,
  downloadingPdf = false,
}: {
  month: string;
  value: number;
  isCurrent?: boolean;
  changePercent?: number;
  changeValue?: number;
  onClick?: () => void;
  onDownloadPdf?: () => void;
  downloadingPdf?: boolean;
}) {
  const hasChange = typeof changePercent === 'number';
  const isUp = hasChange && changePercent >= 0;
  const isNeutral = hasChange && Math.abs(changePercent) < 0.05;

  return (
    <Card
      onClick={onClick}
      sx={{
        position: 'relative',
        borderRadius: '10px',
        border: `1px solid ${isCurrent ? green : border}`,
        boxShadow: isCurrent
          ? '0 4px 14px rgba(47,125,61,0.15)'
          : '0 2px 8px rgba(0,0,0,0.08)',
        height: monthCardHeight,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        backgroundColor: isCurrent ? '#f8fff9' : '#ffffff',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:hover': onClick ? {
          borderColor: green,
          boxShadow: '0 4px 14px rgba(47,125,61,0.12)',
        } : {},
      }}
    >
      {onDownloadPdf && (
        <Tooltip title="Last ned lagerverdi som PDF (bilag)" arrow>
          <span style={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}>
            <IconButton
              size="small"
              disabled={downloadingPdf}
              onClick={(e) => {
                e.stopPropagation();
                onDownloadPdf();
              }}
              sx={{ color: red, '&:hover': { color: red, backgroundColor: 'rgba(213,0,50,0.08)' } }}
            >
              {downloadingPdf ? (
                <CircularProgress size={16} thickness={5} sx={{ color: red }} />
              ) : (
                <PictureAsPdfOutlinedIcon sx={{ fontSize: 19 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}

      <CardContent sx={{ p: 2, height: '100%', minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, color: textMuted, mb: 1, pr: onDownloadPdf ? 3 : 0 }} noWrap>
          {month}
        </Typography>

        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 700,
            color: textMain,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {formatShort(value)}
        </Typography>

        <Typography
          sx={{
            fontSize: 12,
            color: textMuted,
            mt: 0.75,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {formatCurrency(value)}
        </Typography>

        {hasChange && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '86px minmax(0, 1fr)',
              alignItems: 'center',
              columnGap: 0.5,
              mt: 1,
              width: '100%',
              minHeight: 24,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.3,
                px: 0.75,
                py: 0.25,
                width: 86,
                borderRadius: '6px',
                backgroundColor: (isUp || isNeutral) ? '#eaf6ec' : '#fdecec',
                color: (isUp || isNeutral) ? green : red,
              }}
            >
              {isUp ? (
                <TrendingUpIcon sx={{ fontSize: 14 }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: 14 }} />
              )}
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'inherit' }}>
                {changePercent > 0 ? '+' : ''}{changePercent.toFixed(1)}%
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: 11,
                color: textMuted,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {typeof changeValue === 'number'
                ? `(${changeValue > 0 ? '+' : ''}${formatShort(changeValue)})`
                : ''}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function NV04Page() {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const screenSize: UISize = useSelector((state) => (state as AppState).ui.screenSize);
  const open = screenSize.width > 0 && screenSize.width < 1200 ? false : true;
  const button: boolean = useSelector(
    (state) => (state as AppState).ui.isSandwichButtonTurnedOn
  );

  const { snapshot, isFetching, error } = useSelector((state: AppState) => state.NV04);

  const myCompanyId = Number(CSharpUtils.T_companyID) || 0;

  // Fetch firm list to detect parent/child
  const firmListFromAdmin: FirmNavnId[] = useSelector((state) => (state as AppState).userAdmin.firmListFromAdmin);
  const childCompanies = firmListFromAdmin.filter((f) => Number(f.parentFirmID) === myCompanyId && Number(f.firmID) !== myCompanyId);
  const isParent = childCompanies.length > 0;
  const [selectedCompanyId, setSelectedCompanyId] = useState(myCompanyId);

  useEffect(() => {
    dispatch(actions.firmListFromDBGet());
  }, [dispatch]);

  const handleChildToggle = (firmId: number) => {
    setSelectedCompanyId(firmId);
  };

  const currentYear = new Date().getFullYear();
  const currentMonthNum = new Date().getMonth() + 1; // 1-based (Jan=1)
  const previousMonth = currentMonthNum === 1 ? 12 : currentMonthNum - 1;
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(previousMonth);

  useEffect(() => {
    dispatch(fetchSnapshotRequest(selectedCompanyId, selectedYear, selectedMonth));
    return () => {
      dispatch(clearNV04Data());
    };
  }, [dispatch, selectedYear, selectedCompanyId, selectedMonth]);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    setSelectedMonth(year === currentYear ? previousMonth : 12);
  };

  const handleMonthClick = (month: number) => {
    setSelectedMonth(month);
  };

  const [downloadingPdfMonth, setDownloadingPdfMonth] = useState<number | null>(null);

  const handleDownloadPdf = async (year: number, month: number, monthName: string) => {
    setDownloadingPdfMonth(month);
    try {
      const response = await NV04APICalls.fetchWarehouseValuePdf(selectedCompanyId, year, month);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const el = document.createElement('a');
      el.href = url;
      el.download = `Bilag-Lagerverdi-${monthName}-${year}.pdf`;
      document.body.appendChild(el);
      el.click();
      document.body.removeChild(el);
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch {
      alert('Kunne ikke laste ned lagerverdi-PDF. Vennligst prøv igjen.');
    } finally {
      setDownloadingPdfMonth(null);
    }
  };

  const monthlyOverview = snapshot?.monthlyOverview ?? [];
  const warehouseDetails = snapshot?.warehouseDetails ?? [];
  const currentMonth = new Date().getMonth(); // 0-based

  // Compute total from all months with data
  const totalValue = monthlyOverview
    .filter((m) => m.hasData)
    .reduce((sum, m) => sum + m.value, 0);

  // Find best warehouse
  const bestWh = warehouseDetails.length > 0
    ? warehouseDetails.reduce((best, w) => (w.warehouseValue > best.warehouseValue ? w : best), warehouseDetails[0])
    : null;

  return (
    <div className={classes.root}>
      <ShiftHome />
      <CssBaseline />
      <LPDrawer />

      <main className={clsx(classes.content, { [classes.contentShift]: button ? true : open })}>
        <LPAppBar ismenuvisible={true} />

        <Box sx={{ p: 2.5, height: 'calc(100vh - 50px)', overflowY: 'auto', overflowX: 'hidden' }}>
          {isFetching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
              <CircularProgress />
            </Box>
          )}

          {error && !isFetching && (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography sx={{ color: red, fontSize: 16 }}>{String(error)}</Typography>
            </Box>
          )}

          {!isFetching && !error && (
          <Box
            sx={{
              backgroundColor: bgLight,
              border: `1px solid ${border}`,
              borderRadius: '10px',
              p: 2.5,
            }}
          >
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: 30, fontWeight: 700, color: textMain, mb: 0.5 }}>
                Lagerverdi per måned 
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
              {years.map((year) => (
                <Chip
                  key={year}
                  label={year}
                  clickable
                  onClick={() => handleYearChange(year)}
                  sx={{
                    fontWeight: 700,
                    fontSize: 14,
                    px: 1,
                    borderRadius: '20px',
                    backgroundColor: selectedYear === year ? red : '#fff',
                    color: selectedYear === year ? '#fff' : textMain,
                    border: `1px solid ${selectedYear === year ? red : border}`,
                    '&:hover': {
                      backgroundColor: selectedYear === year ? red : '#f0f0f0',
                    },
                  }}
                />
              ))}
            </Box>

            {isParent && (
            <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: textMain }}>
                Selskap:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <Select
                  value={selectedCompanyId}
                  onChange={(e) => handleChildToggle(Number(e.target.value))}
                  sx={{
                    borderRadius: '8px',
                    fontSize: 14,
                    backgroundColor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: border },
                  }}
                >
                  <MenuItem value={myCompanyId}>
                    {firmListFromAdmin.find((f) => Number(f.firmID) === myCompanyId)?.firmName || `Firma ${myCompanyId}`} (Hovedselskap)
                  </MenuItem>
                  {childCompanies.map((child) => (
                    <MenuItem key={child.firmID} value={Number(child.firmID)}>
                      {child.firmName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            )}

            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: textMain }}>
                  Månedsoversikt
                </Typography>
                <Tooltip
                  title="Viser lagerverdien per siste dag i hver måned. Velg år dersom du vil gå tilbake i tid."
                  arrow
                  placement="right"
                >
                  <IconButton size="small" sx={{ color: textMuted }}>
                    <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography sx={{ fontSize: 13, color: textMuted, mt: 0.25 }}>
                Viser verdi per siste med endring fra foregående måned.
              </Typography>
             </Box>

            <Grid container spacing={2}>
              {monthlyOverview
                .filter((item) => selectedYear < currentYear || item.month < currentMonthNum)
                .map((item) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.month} sx={{ minWidth: 0, display: 'flex' }}>
                  <MonthCard
                    month={item.monthName}
                    value={item.value}
                    isCurrent={item.month === selectedMonth}
                    changePercent={item.hasData ? item.changeFromPreviousMonthPercent : undefined}
                    changeValue={item.hasData ? item.changeFromPreviousMonthValue : undefined}
                    onClick={() => handleMonthClick(item.month)}
                    onDownloadPdf={
                      item.hasData && isPdfAvailableFor(selectedYear, item.month)
                        ? () => handleDownloadPdf(selectedYear, item.month, item.monthName)
                        : undefined
                    }
                    downloadingPdf={downloadingPdfMonth === item.month}
                  />
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mb: 1.5, mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: textMain }}>
                  Lagersted
                </Typography>
                <Tooltip
                  title="Viser fordelingen per lagersted for valgt periode. Valgt periode markeres med en grønn ramme. For å se fordelingen for en bestemt måned, klikker du på ønsket måned slik at den får grønn ramme. NB: Denne funksjonen er tilgjengelig fra Mai 2026."
                  arrow
                  placement="right"
                >
                  <IconButton size="small" sx={{ color: textMuted }}>
                    <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography sx={{ fontSize: 13, color: textMuted, mt: 0.25 }}>
                Viser fordeling på lagersted for valgt måned (fra mai 2026).
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {warehouseDetails.map((item) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.warehouseCode}>
                  <WarehouseCard code={String(item.warehouseCode)} value={item.warehouseValue} />
                </Grid>
              ))}
            </Grid>
          </Box>
          )}
        </Box>
      </main>
    </div>
  );
}